/* /app/courtage — module Courtage
 * Mandats (immobilier + entreprise/M&A) avec pipeline visuel par statut,
 * qualification (score 0-100), fiche entreprise + valorisation par multiples,
 * lecture IA NOAM (acheteurs stratégiques + fourchette de valo).
 */
import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import AiText from "@/components/AiText";
import { useTranslation } from "react-i18next";
import {
  Plus, Trash2, Loader2, Briefcase, Sparkles, ChevronLeft, X, Save, Edit3,
  Building, Users, TrendingUp, Link as LinkIcon, Wand2, Lightbulb, Handshake, MapPin,
  FileDown, Mail, Copy,
} from "lucide-react";
import CourtageAiIntake from "@/components/CourtageAiIntake";
import { ShareToChatButton } from "@/components/ShareToChat";
import AnnuaireEntitySelector from "@/components/AnnuaireEntitySelector";
import DocumentAttachZone from "@/components/DocumentAttachZone";
import CourtageDataRoom from "@/components/CourtageDataRoom";
import DealScorePanel from "@/components/DealScorePanel";
import MarketSizeWidget from "@/components/MarketSizeWidget";
import ModuleHelpButton from "@/components/ModuleHelpButton";
import ThreeQPageHeader from "@/components/ThreeQPageHeader";

const MANDATE_TYPES = ["courtage_immo", "cession_entreprise", "acquisition", "ma"];
const STATUS_LIST = ["prospection", "signe", "en_cours", "compromis", "acte", "cloture", "abandonne"];
const ACTIVE_STATUSES = ["prospection", "signe", "en_cours", "compromis", "acte"];

const fmtChf = (n) => {
  if (n === null || n === undefined || isNaN(Number(n))) return "—";
  return Math.round(Number(n)).toLocaleString("fr-CH").replace(/,/g, "'") + " CHF";
};

export default function Courtage() {
  const { t } = useTranslation();
  const [data, setData] = useState({ items: [], kpi: {}, is_empty: true });
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [aiIntakeOpen, setAiIntakeOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/workspace/courtage/mandates"); setData(r.data); }
    catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  if (activeId) {
    return <MandateDetail id={activeId} onBack={() => { setActiveId(null); load(); }} />;
  }

  const k = data.kpi || {};
  const items = data.items || [];

  return (
    <div className="space-y-6" data-testid="courtage-root">
      <ThreeQPageHeader endpoint="/workspace/courtage/top-priority" />
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[#378ADD] text-[10px] uppercase tracking-[0.2em] font-semibold flex items-center gap-1.5">
            <Briefcase className="w-3 h-3" /> {t("court.overline", { defaultValue: "Tactique" })}
          </div>
          <h1 className="noam-display text-3xl font-bold mt-1">{t("court.title", { defaultValue: "Courtage" })}</h1>
          <p className="text-zinc-400 text-sm mt-1 max-w-2xl">
            {t("court.subtitle", { defaultValue: "Pipeline de mandats (courtage immobilier et cession d'entreprises) avec qualification, valorisation par multiples et lecture IA." })}
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setAiIntakeOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-md border border-[#7F77DD]/50 bg-[#7F77DD]/10 text-[#B5AEF7] hover:bg-[#7F77DD]/20 transition"
            data-testid="court-ai-intake-btn">
            <Wand2 className="w-3.5 h-3.5" /> Projet express (IA)
          </button>
          <button onClick={() => setCreating(true)} className="btn-primary" data-testid="court-new-btn">
            <Plus className="w-3.5 h-3.5" /> {t("court.new_mandate", { defaultValue: "Nouveau mandat" })}
          </button>
        </div>
      </div>

      <div className="flex justify-end">
        <ModuleHelpButton moduleKey="courtage" />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Kpi label={t("court.kpi_active", { defaultValue: "Actifs" })} value={k.active_count ?? 0} />
        <Kpi label={t("court.kpi_total", { defaultValue: "Total" })} value={k.total_count ?? 0} />
        <Kpi label={t("court.kpi_volume", { defaultValue: "Volume pipeline" })} value={fmtChf(k.volume_pipeline_chf)} />
        <Kpi label={t("court.kpi_commission", { defaultValue: "Commission estimée" })} value={fmtChf(k.commission_estimee_chf)} />
        <Kpi label={t("court.kpi_conversion", { defaultValue: "Conversion" })} value={`${k.taux_conversion_pct ?? 0} %`} />
      </div>

      <MarketSizeWidget moduleKey="courtage" title={t("court.title", { defaultValue: "Courtage" })} />

      {loading ? (
        <Loading />
      ) : data.is_empty ? (
        <EmptyState
          icon={Briefcase}
          title={t("court.empty_title", { defaultValue: "Aucun mandat" })}
          desc={t("court.empty_desc", { defaultValue: "Lancez un mandat pour tracer le volume, la commission et la qualité du pipeline." })}
          cta={t("court.new_mandate", { defaultValue: "Nouveau mandat" })}
          onCta={() => setCreating(true)}
          testid="court-empty"
        />
      ) : (
        <PipelineBoard items={items} onOpen={setActiveId} />
      )}

      {creating && <NewMandateModal onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); setActiveId(id); }} />}
      {aiIntakeOpen && <CourtageAiIntake onClose={() => setAiIntakeOpen(false)} onCreated={(id) => { setAiIntakeOpen(false); if (id) setActiveId(id); else load(); }} />}
    </div>
  );
}

function PipelineBoard({ items, onOpen }) {
  const { t } = useTranslation();
  const groups = useMemo(() => {
    const out = {};
    STATUS_LIST.forEach((s) => (out[s] = []));
    items.forEach((m) => {
      const s = STATUS_LIST.includes(m.statut) ? m.statut : "prospection";
      out[s].push(m);
    });
    return out;
  }, [items]);

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="court-pipeline">
      {STATUS_LIST.map((s) => (
        <div key={s} className="noam-card p-3 min-h-[200px]" data-testid={`court-col-${s}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">
              {t(`court.status_${s}`, { defaultValue: s })}
            </div>
            <span className="text-[10px] text-zinc-600">{groups[s].length}</span>
          </div>
          <div className="space-y-2">
            {groups[s].map((m) => (
              <button key={m.id} onClick={() => onOpen(m.id)}
                className="w-full text-left p-2 rounded bg-white/5 hover:bg-white/10 transition-colors"
                data-testid={`court-mandate-${m.id}`}>
                <div className="text-xs font-semibold truncate">{m.title}</div>
                <div className="text-[10px] text-zinc-500 mt-0.5 truncate">{m.client || "—"}</div>
                <div className="text-[10px] text-[#378ADD] mt-1">{fmtChf(m.valeur_transaction_chf)}</div>
                {(m.specialties || []).length > 0 && (
                  <div className="flex flex-wrap gap-0.5 mt-1">
                    {(m.specialties || []).slice(0, 3).map((sp) => (
                      <span key={sp} className="text-[9px] px-1 py-0 rounded bg-[#EF9F27]/15 text-[#EF9F27] border border-[#EF9F27]/30">
                        {sp.replace(/_/g, " ")}
                      </span>
                    ))}
                    {(m.specialties || []).length > 3 && (
                      <span className="text-[9px] text-zinc-500">+{m.specialties.length - 3}</span>
                    )}
                  </div>
                )}
                {m.deal_id && (
                  <div className="text-[9px] text-[#1D9E75] mt-1 inline-flex items-center gap-0.5">
                    <LinkIcon className="w-2.5 h-2.5" /> deal
                  </div>
                )}
              </button>
            ))}
            {groups[s].length === 0 && (
              <div className="text-[10px] text-zinc-600 italic text-center py-3">—</div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function NewMandateModal({ onClose, onCreated }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    title: "", type: "courtage_immo", statut: "prospection",
    client: "", contrepartie: "",
    valeur_transaction_chf: 0, commission_pct: 3,
    deal_id: null, notes: "",
    specialties: [],  // V23.15.78 — pastilles cumulables
  });
  const [deals, setDeals] = useState([]);
  const [specialtiesCatalog, setSpecialtiesCatalog] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/workspace/origination/pipeline").then((r) => {
      const all = Object.values(r.data.by_step || {}).flat();
      setDeals(all);
    }).catch(() => {});
    // V23.15.78 — Récupère le catalogue de pastilles depuis /courtage/mandates
    api.get("/workspace/courtage/mandates").then((r) => {
      setSpecialtiesCatalog(r.data.specialties || []);
    }).catch(() => {});
  }, []);

  const toggleSpecialty = (s) => {
    setForm((f) => {
      const cur = f.specialties || [];
      return { ...f, specialties: cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/workspace/courtage/mandates", { ...form, deal_id: form.deal_id || null });
      onCreated(r.data.id);
    } catch (e) { notify.error(fmtError(e)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="court-new-modal">
      <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="noam-card p-6 w-full max-w-md space-y-3 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="noam-display text-lg font-semibold">{t("court.new_mandate", { defaultValue: "Nouveau mandat" })}</div>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder={t("court.field_title", { defaultValue: "Titre du mandat" })} className="noam-input" data-testid="court-modal-title" />
        <div className="grid grid-cols-2 gap-2">
          <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="noam-input" data-testid="court-modal-type">
            {MANDATE_TYPES.map((p) => <option key={p} value={p}>{t(`court.type_${p}`, { defaultValue: p })}</option>)}
          </select>
          <select value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })} className="noam-input">
            {STATUS_LIST.map((p) => <option key={p} value={p}>{t(`court.status_${p}`, { defaultValue: p })}</option>)}
          </select>
        </div>
        <input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })}
          placeholder={t("court.field_client", { defaultValue: "Client (vendeur/acquéreur)" })} className="noam-input" />
        <input value={form.contrepartie} onChange={(e) => setForm({ ...form, contrepartie: e.target.value })}
          placeholder={t("court.field_contrepartie", { defaultValue: "Contrepartie" })} className="noam-input" />
        {/* V23.15.78 — Pastilles cumulables de spécialisation métier */}
        {specialtiesCatalog.length > 0 && (
          <div>
            <label className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">
              {t("court.field_specialties", { defaultValue: "Spécialisations (cumulables)" })}
            </label>
            <div className="flex flex-wrap gap-1.5" data-testid="court-modal-specialties">
              {specialtiesCatalog.map((s) => {
                const checked = (form.specialties || []).includes(s);
                const label = s.replace(/_/g, " ");
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => toggleSpecialty(s)}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                      checked
                        ? "bg-[#EF9F27]/20 border-[#EF9F27] text-[#EF9F27] font-semibold"
                        : "bg-black/30 border-white/10 text-zinc-400 hover:border-[#EF9F27]/50 hover:text-white"
                    }`}
                    data-testid={`court-modal-specialty-${s}`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <NumField label={t("court.field_value", { defaultValue: "Valeur transaction (CHF)" })} value={form.valeur_transaction_chf} onChange={(v) => setForm({ ...form, valeur_transaction_chf: v })} testid="court-modal-value" />
          <NumField label={t("court.field_commission", { defaultValue: "Commission (%)" })} value={form.commission_pct} onChange={(v) => setForm({ ...form, commission_pct: v })} />
        </div>
        {deals.length > 0 && (
          <select value={form.deal_id || ""} onChange={(e) => setForm({ ...form, deal_id: e.target.value || null })}
            className="noam-input" data-testid="court-modal-deal">
            <option value="">{t("court.no_deal", { defaultValue: "Aucun deal lié" })}</option>
            {deals.map((d) => (<option key={d.id} value={d.id}>{d.titre}</option>))}
          </select>
        )}
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
          placeholder={t("court.field_notes", { defaultValue: "Notes" })} className="noam-input resize-none" />
        <button type="submit" disabled={busy || !form.title.trim()} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="court-modal-submit">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
          {t("common.create", { defaultValue: "Créer" })}
        </button>
      </form>
    </div>
  );
}

/* =========================== Detail ============================ */

function MandateDetail({ id, onBack }) {
  const { t } = useTranslation();
  const [mandate, setMandate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [aiBusy, setAiBusy] = useState(false);
  const [teaserBusy, setTeaserBusy] = useState(false);
  const [tab, setTab] = useState("info");

  const downloadTeaser = async () => {
    setTeaserBusy(true);
    try {
      const resp = await api.get(`/workspace/courtage/mandates/${id}/teaser.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      // Nom anonymisé : commune seule (l'adresse exacte ne doit pas fuiter, même dans le nom du fichier)
      const cd = resp.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="([^"]+)"/);
      a.download = m ? m[1] : `teaser_${id.slice(0, 8)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { notify.error(fmtError(e)); }
    finally { setTeaserBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/workspace/courtage/mandates/${id}`); setMandate(r.data); }
    catch (e) { notify.error(fmtError(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async (patch) => {
    try { const r = await api.patch(`/workspace/courtage/mandates/${id}`, patch); setMandate(r.data); }
    catch (e) { notify.error(fmtError(e)); }
  };
  const remove = async () => {
    if (!window.confirm(t("court.confirm_delete", { defaultValue: "Supprimer ce mandat ?" }))) return;
    try { await api.delete(`/workspace/courtage/mandates/${id}`); notify.success("Supprimé"); onBack(); }
    catch (e) { notify.error(fmtError(e)); }
  };
  const runAI = async () => {
    setAiBusy(true);
    try { const r = await api.post(`/workspace/courtage/mandates/${id}/ai-analysis`); setMandate((m) => ({ ...m, ai_reading: r.data.ai_reading })); notify.success(t("court.ai_done", { defaultValue: "Analyse IA prête." })); }
    catch (e) { notify.error(fmtError(e)); }
    setAiBusy(false);
  };

  if (loading || !mandate) {
    return <div className="text-zinc-400 text-sm flex items-center gap-2" data-testid="court-detail-loading"><Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}</div>;
  }

  return (
    <div className="space-y-6" data-testid="court-detail-root">
      <div className="flex items-center justify-between">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-white" data-testid="court-detail-back">
          <ChevronLeft className="w-3.5 h-3.5" /> {t("common.back", { defaultValue: "Retour" })}
        </button>
        <div className="flex gap-2">
          <button onClick={downloadTeaser} disabled={teaserBusy} className="btn-secondary text-xs disabled:opacity-50" title="Plaquette de vente anonymisée" data-testid="court-detail-teaser-btn">
            {teaserBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileDown className="w-3 h-3" />} Teaser PDF
          </button>
          <ShareToChatButton attachment={{ kind: "mandat", id: mandate.id }} title={mandate.title} compact={false} className="btn-secondary text-xs text-zinc-400 hover:text-[#1D9E75]" />
          <button onClick={runAI} disabled={aiBusy} className="btn-primary text-xs disabled:opacity-50" data-testid="court-detail-ai-btn">
            {aiBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} {t("court.ai_btn", { defaultValue: "Lecture IA" })}
          </button>
          <button onClick={remove} className="btn-secondary text-xs text-red-400 hover:text-red-300" data-testid="court-detail-delete">
            <Trash2 className="w-3 h-3" /> {t("common.delete", { defaultValue: "Supprimer" })}
          </button>
        </div>
      </div>

      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <input value={mandate.title} onChange={(e) => setMandate({ ...mandate, title: e.target.value })}
            onBlur={() => save({ title: mandate.title })}
            className="bg-transparent border-0 noam-display text-2xl font-bold w-full focus:outline-none"
            data-testid="court-detail-title-input" />
          <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500">
            <select value={mandate.type} onChange={(e) => { setMandate({ ...mandate, type: e.target.value }); save({ type: e.target.value }); }}
              className="noam-input text-xs py-1 px-2 w-auto">
              {MANDATE_TYPES.map((p) => <option key={p} value={p}>{t(`court.type_${p}`, { defaultValue: p })}</option>)}
            </select>
            <select value={mandate.statut} onChange={(e) => { setMandate({ ...mandate, statut: e.target.value }); save({ statut: e.target.value }); }}
              className="noam-input text-xs py-1 px-2 w-auto" data-testid="court-detail-statut">
              {STATUS_LIST.map((p) => <option key={p} value={p}>{t(`court.status_${p}`, { defaultValue: p })}</option>)}
            </select>
          </div>
        </div>
        {mandate.qualification_score && <QualBadge q={mandate.qualification_score} />}
      </div>

      <div className="flex gap-1 border-b border-white/5">
        {[
          { id: "info", label: t("court.tab_info", { defaultValue: "Infos & valeur" }), icon: Briefcase },
          { id: "qualification", label: t("court.tab_qual", { defaultValue: "Qualification" }), icon: TrendingUp },
          { id: "company", label: t("court.tab_company", { defaultValue: "Fiche entreprise (M&A)" }), icon: Building },
          { id: "dataroom", label: t("court.tab_dataroom", { defaultValue: "Data Room" }), icon: FileDown },
          { id: "score", label: t("court.tab_score", { defaultValue: "Réalisabilité" }), icon: Sparkles },
        ].map((tt) => {
          const Icon = tt.icon;
          const active = tab === tt.id;
          return (
            <button key={tt.id} onClick={() => setTab(tt.id)}
              data-testid={`court-tab-${tt.id}`}
              className={`px-4 py-2 text-xs font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                active ? "border-[#378ADD] text-white" : "border-transparent text-zinc-500 hover:text-zinc-300"
              }`}>
              <Icon className="w-3.5 h-3.5" /> {tt.label}
            </button>
          );
        })}
      </div>

      {tab === "info" && <InfoTab mandate={mandate} onSave={save} />}
      {tab === "qualification" && <QualificationTab mandate={mandate} onReload={load} />}
      {tab === "company" && <CompanyTab mandate={mandate} onReload={load} />}
      {tab === "dataroom" && <CourtageDataRoom mandate={mandate} />}
      {tab === "score" && <DealScorePanel endpoint={`/workspace/courtage/mandates/${mandate.id}`} />}

      <ProjectIntel mandate={mandate} />

      <div className="noam-card p-4" data-testid="court-entities-card">
        <AnnuaireEntitySelector
          value={mandate.annuaire_entity_ids || []}
          onChange={(ids) => { setMandate({ ...mandate, annuaire_entity_ids: ids }); save({ annuaire_entity_ids: ids }); }}
          testid="court-entity-selector"
          label={t("court.entities_label", { defaultValue: "Parties prenantes — Annuaire" })}
          placeholder={t("court.entities_ph", { defaultValue: "Rechercher client, contrepartie, intermédiaire…" })}
        />
      </div>

      <DocumentAttachZone module="courtage" entityId={mandate.id} entityLabel={mandate.title} />

      {mandate.ai_reading && (
        <div className="noam-card p-5" data-testid="court-ai-reading">
          <div className="text-[#378ADD] text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> {t("court.ai_reading", { defaultValue: "Lecture IA" })}
          </div>
          <pre className="whitespace-pre-wrap text-sm text-zinc-200 mt-2 font-sans"><AiText>{mandate.ai_reading}</AiText></pre>
        </div>
      )}
    </div>
  );
}

function InfoTab({ mandate, onSave }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    client: mandate.client || "",
    contrepartie: mandate.contrepartie || "",
    valeur_transaction_chf: mandate.valeur_transaction_chf || 0,
    commission_pct: mandate.commission_pct || 3,
    entree_at: mandate.entree_at || "",
    closing_estime_at: mandate.closing_estime_at || "",
    notes: mandate.notes || "",
  });
  const commit = () => onSave(form);
  const commission = (form.valeur_transaction_chf * form.commission_pct) / 100;

  return (
    <div className="space-y-4" data-testid="court-info-tab">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="noam-card p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("court.parties", { defaultValue: "Parties" })}</div>
          <input value={form.client} onChange={(e) => setForm({ ...form, client: e.target.value })} onBlur={commit}
            placeholder={t("court.field_client", { defaultValue: "Client" })} className="noam-input" />
          <input value={form.contrepartie} onChange={(e) => setForm({ ...form, contrepartie: e.target.value })} onBlur={commit}
            placeholder={t("court.field_contrepartie", { defaultValue: "Contrepartie" })} className="noam-input" />
        </div>
        <div className="noam-card p-4 space-y-2">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("court.financial", { defaultValue: "Financier" })}</div>
          <NumField label={t("court.field_value", { defaultValue: "Valeur transaction (CHF)" })}
            value={form.valeur_transaction_chf}
            onChange={(v) => setForm({ ...form, valeur_transaction_chf: v })}
            onBlur={commit} />
          <NumField label={t("court.field_commission", { defaultValue: "Commission (%)" })}
            value={form.commission_pct}
            onChange={(v) => setForm({ ...form, commission_pct: v })}
            onBlur={commit} />
          <div className="text-xs text-zinc-400 flex justify-between pt-2 border-t border-white/5">
            <span>{t("court.commission_calc", { defaultValue: "Commission estimée" })}</span>
            <span className="text-[#378ADD] font-semibold">{fmtChf(commission)}</span>
          </div>
        </div>
      </div>
      <div className="noam-card p-4 space-y-2">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500">{t("court.timing", { defaultValue: "Calendrier" })}</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <div className="text-[10px] text-zinc-500 mb-1">{t("court.entree", { defaultValue: "Entrée mandat" })}</div>
            <input type="date" value={form.entree_at || ""} onChange={(e) => setForm({ ...form, entree_at: e.target.value })} onBlur={commit} className="noam-input" />
          </label>
          <label className="block">
            <div className="text-[10px] text-zinc-500 mb-1">{t("court.closing", { defaultValue: "Closing estimé" })}</div>
            <input type="date" value={form.closing_estime_at || ""} onChange={(e) => setForm({ ...form, closing_estime_at: e.target.value })} onBlur={commit} className="noam-input" />
          </label>
        </div>
      </div>
      <div className="noam-card p-4">
        <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">{t("court.notes", { defaultValue: "Notes" })}</div>
        <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} onBlur={commit}
          rows={3} className="noam-input resize-none w-full" data-testid="court-info-notes" />
      </div>
    </div>
  );
}

function QualificationTab({ mandate, onReload }) {
  const { t } = useTranslation();
  const initial = mandate.qualification || {
    motivation_vendeur: 5, alignement_prix_marche: 5, acheteurs_identifies: 0,
    complexite: 5, liquidite_marche: 5, notes: "",
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try { await api.put(`/workspace/courtage/mandates/${mandate.id}/qualification`, form); notify.success(t("common.saved", { defaultValue: "Enregistré." })); onReload(); }
    catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  return (
    <div className="noam-card p-5 space-y-4" data-testid="court-qual-tab">
      <p className="text-xs text-zinc-400">
        {t("court.qual_help", { defaultValue: "Évaluez chaque dimension de 1 à 10. Le score combiné prédit la probabilité de closing." })}
      </p>
      <Slider label={t("court.qual_motivation", { defaultValue: "Motivation vendeur" })}
        value={form.motivation_vendeur} onChange={(v) => setForm({ ...form, motivation_vendeur: v })}
        testid="court-qual-motivation" />
      <Slider label={t("court.qual_alignement", { defaultValue: "Alignement prix vs marché" })}
        value={form.alignement_prix_marche} onChange={(v) => setForm({ ...form, alignement_prix_marche: v })} />
      <div className="grid grid-cols-2 gap-2">
        <NumField label={t("court.qual_acheteurs", { defaultValue: "Acheteurs identifiés (n)" })}
          value={form.acheteurs_identifies} onChange={(v) => setForm({ ...form, acheteurs_identifies: parseInt(v) || 0 })}
          testid="court-qual-acheteurs" />
        <Slider label={t("court.qual_complexite", { defaultValue: "Complexité (1=facile)" })}
          value={form.complexite} onChange={(v) => setForm({ ...form, complexite: v })} />
      </div>
      <Slider label={t("court.qual_liquidite", { defaultValue: "Liquidité du marché" })}
        value={form.liquidite_marche} onChange={(v) => setForm({ ...form, liquidite_marche: v })} />
      <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })}
        placeholder={t("court.field_notes", { defaultValue: "Notes" })} rows={2}
        className="noam-input resize-none w-full" />
      <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="court-qual-save">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
        {t("common.save", { defaultValue: "Enregistrer" })}
      </button>
    </div>
  );
}

function CompanyTab({ mandate, onReload }) {
  const { t } = useTranslation();
  const initial = mandate.company_file || {
    secteur: "", chiffre_affaires_chf: 0, ebitda_chf: 0, effectif: 0,
    multiple_ebitda: 5.0, multiple_ca: 1.0, acheteurs_potentiels: [],
  };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [acheteurInput, setAcheteurInput] = useState("");

  const submit = async () => {
    if (!form.secteur.trim()) { notify.error(t("court.need_secteur", { defaultValue: "Le secteur est requis." })); return; }
    setBusy(true);
    try { await api.put(`/workspace/courtage/mandates/${mandate.id}/company`, form); notify.success(t("common.saved", { defaultValue: "Enregistré." })); onReload(); }
    catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };

  const addAcheteur = () => {
    if (!acheteurInput.trim()) return;
    setForm({ ...form, acheteurs_potentiels: [...(form.acheteurs_potentiels || []), acheteurInput.trim()] });
    setAcheteurInput("");
  };
  const removeAcheteur = (i) => setForm({ ...form, acheteurs_potentiels: form.acheteurs_potentiels.filter((_, idx) => idx !== i) });

  const val = mandate.valuation;

  return (
    <div className="space-y-4" data-testid="court-company-tab">
      <div className="noam-card p-5 space-y-3">
        <div className="noam-display text-base font-semibold">{t("court.company_data", { defaultValue: "Données entreprise" })}</div>
        <input required value={form.secteur} onChange={(e) => setForm({ ...form, secteur: e.target.value })}
          placeholder={t("court.field_secteur", { defaultValue: "Secteur" })} className="noam-input" data-testid="court-company-secteur" />
        <div className="grid grid-cols-2 gap-2">
          <NumField label={t("court.field_ca", { defaultValue: "Chiffre d'affaires (CHF)" })} value={form.chiffre_affaires_chf} onChange={(v) => setForm({ ...form, chiffre_affaires_chf: v })} testid="court-company-ca" />
          <NumField label={t("court.field_ebitda", { defaultValue: "EBITDA (CHF)" })} value={form.ebitda_chf} onChange={(v) => setForm({ ...form, ebitda_chf: v })} testid="court-company-ebitda" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <NumField label={t("court.field_effectif", { defaultValue: "Effectif" })} value={form.effectif} onChange={(v) => setForm({ ...form, effectif: parseInt(v) || 0 })} />
          <NumField label={t("court.field_mult_ebitda", { defaultValue: "Mult. EBITDA" })} value={form.multiple_ebitda} onChange={(v) => setForm({ ...form, multiple_ebitda: v })} />
          <NumField label={t("court.field_mult_ca", { defaultValue: "Mult. CA" })} value={form.multiple_ca} onChange={(v) => setForm({ ...form, multiple_ca: v })} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{t("court.acheteurs_potentiels", { defaultValue: "Acheteurs potentiels" })}</div>
          <div className="flex gap-2 mb-2">
            <input value={acheteurInput} onChange={(e) => setAcheteurInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAcheteur(); } }}
              placeholder={t("court.add_acheteur", { defaultValue: "Ajouter un acheteur stratégique…" })} className="noam-input flex-1" />
            <button type="button" onClick={addAcheteur} className="btn-secondary text-xs"><Plus className="w-3 h-3" /></button>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(form.acheteurs_potentiels || []).map((a, i) => (
              <span key={i} className="text-xs px-2 py-1 rounded bg-white/5 text-zinc-300 inline-flex items-center gap-1">
                {a}
                <button type="button" onClick={() => removeAcheteur(i)} className="text-zinc-500 hover:text-red-400"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        </div>
        <button onClick={submit} disabled={busy} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="court-company-save">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          {t("common.save", { defaultValue: "Enregistrer" })}
        </button>
      </div>

      {val && (
        <div className="noam-card p-5" data-testid="court-valuation-panel">
          <div className="noam-display text-base font-semibold mb-3">{t("court.valuation_title", { defaultValue: "Valorisation par multiples" })}</div>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Line k={t("court.val_ev_ebitda", { defaultValue: "EV/EBITDA" })} v={fmtChf(val.valeur_ev_ebitda_chf)} />
            <Line k={t("court.val_ev_ca", { defaultValue: "EV/CA" })} v={fmtChf(val.valeur_ev_ca_chf)} />
            <Line k={t("court.val_low", { defaultValue: "Fourchette basse" })} v={fmtChf(val.fourchette_low_chf)} />
            <Line k={t("court.val_high", { defaultValue: "Fourchette haute" })} v={fmtChf(val.fourchette_high_chf)} />
          </div>
          <div className="mt-4 p-4 rounded bg-[#378ADD]/10 text-center">
            <div className="text-[10px] uppercase tracking-wider text-[#378ADD]">{t("court.val_mediane", { defaultValue: "Valeur médiane" })}</div>
            <div className="text-2xl font-bold mt-1" data-testid="court-val-mediane">{fmtChf(val.valeur_mediane_chf)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================== Shared bits ============================ */

/* V23.16 — Intelligence projet (fiche immo, marché, note, acheteurs, négo, métacognition). */
/* V23.16 — Mini-CRM acheteurs : statuts + actions par acheteur pressenti. */
const OUTREACH_META = {
  teaser_envoye: { label: "Teaser envoyé", color: "#378ADD" },
  relance: { label: "Relance", color: "#EF9F27" },
  reponse_recue: { label: "Réponse reçue", color: "#7F77DD" },
  interesse: { label: "Intéressé", color: "#1D9E75" },
  nda_signe: { label: "NDA signé", color: "#B5AEF7" },
  refus: { label: "Refus", color: "#E24B4A" },
};

function ProjectIntel({ mandate }) {
  const [emailBuyer, setEmailBuyer] = useState(null);
  const [outreach, setOutreach] = useState(mandate.outreach || {});
  const [actionsFor, setActionsFor] = useState(null);
  const im = mandate.immo;
  const ma = mandate.market_analysis;
  const pg = mandate.project_grade;
  const ng = mandate.negotiation;
  const buyers = mandate.annuaire_buyers || [];
  const profiles = mandate.buyer_profiles || [];
  const metacog = mandate.metacognition || [];
  if (!im && !ma && !pg && !ng && buyers.length === 0 && metacog.length === 0) return null;
  const logOutreach = async (buyerId, action) => {
    setActionsFor(null);
    try {
      const r = await api.post(`/workspace/courtage/mandates/${mandate.id}/outreach`, { buyer_id: buyerId, action });
      setOutreach(r.data.outreach || {});
      notify.success(`${OUTREACH_META[action]?.label} — noté.`);
    } catch (e) { notify.error(fmtError(e)); }
  };

  const outreachStats = (() => {
    const vals = Object.values(outreach || {});
    if (vals.length === 0) return null;
    const contacted = vals.length;
    const interested = vals.filter((v) => ["interesse", "nda_signe"].includes(v.status)).length;
    return { contacted, interested };
  })();

  const gradeColor = (note) => {
    const c = (note || "").charAt(0);
    return c === "A" ? "#1D9E75" : c === "B" ? "#378ADD" : c === "C" ? "#EF9F27" : "#E24B4A";
  };
  return (
    <div className="space-y-3" data-testid="court-project-intel">
      <div className="grid md:grid-cols-2 gap-3">
        {im && (
          <div className="noam-card p-4" data-testid="court-intel-immo">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><Building className="w-3 h-3" /> Fiche projet immobilier</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div><div className="text-zinc-600 text-[10px]">Localisation</div><div className="text-zinc-200 flex items-center gap-1"><MapPin className="w-3 h-3 text-[#378ADD]" />{im.localisation || "—"}</div></div>
              <div><div className="text-zinc-600 text-[10px]">Type de bien</div><div className="text-zinc-200 capitalize">{im.type_bien || "—"}</div></div>
              <div><div className="text-zinc-600 text-[10px]">Surface</div><div className="text-zinc-200">{im.surface_m2 ? `${im.surface_m2} m²` : "—"}</div></div>
              <div><div className="text-zinc-600 text-[10px]">Stade</div><div className="text-zinc-200 capitalize">{im.stade || "—"}{im.nb_lots ? ` · ${im.nb_lots} lots` : ""}</div></div>
            </div>
          </div>
        )}
        {(pg || ma) && (
          <div className="noam-card p-4 flex gap-4" data-testid="court-intel-market">
            {pg && (
              <div className="text-center shrink-0">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Note projet</div>
                <div className="text-3xl font-bold noam-display mt-1" style={{ color: gradeColor(pg.note) }}>{pg.note}</div>
              </div>
            )}
            {ma && (
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 flex items-center gap-1.5"><TrendingUp className="w-3 h-3" /> Marché</div>
                <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-zinc-400">
                  {ma.prix_m2_chf > 0 && <span>Prix/m² : <b className="text-zinc-200">{fmtChf(ma.prix_m2_chf)}</b></span>}
                  {ma.fourchette_basse_chf > 0 && <span>Fourchette : <b className="text-zinc-200">{fmtChf(ma.fourchette_basse_chf)} – {fmtChf(ma.fourchette_haute_chf)}</b></span>}
                  {ma.rendement_brut_pct > 0 && <span>Rdt brut : <b className="text-zinc-200">{ma.rendement_brut_pct}%</b></span>}
                  {ma.rendement_potentiel_pct > 0 && <span>Rdt potentiel : <b className="text-[#1D9E75]">{ma.rendement_potentiel_pct}%</b></span>}
                </div>
                {ma.commentaire && <p className="text-[11px] text-zinc-500 mt-1.5 whitespace-pre-wrap">{ma.commentaire}</p>}
              </div>
            )}
          </div>
        )}
      </div>

      {(buyers.length > 0 || profiles.length > 0) && (
        <div className="noam-card p-4" data-testid="court-intel-buyers">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><Users className="w-3 h-3" /> À qui le vendre ?</div>
            {outreachStats && (
              <span className="text-[10px] text-zinc-500" data-testid="court-outreach-stats">
                {outreachStats.contacted} suivi{outreachStats.contacted > 1 ? "s" : ""} · <span className="text-[#1D9E75]">{outreachStats.interested} intéressé{outreachStats.interested > 1 ? "s" : ""}</span>
              </span>
            )}
          </div>
          {buyers.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mb-2">
              {buyers.map((b) => {
                const o = outreach[b.id];
                const meta = o ? OUTREACH_META[o.status] : null;
                return (
                  <span key={b.id} className="relative inline-flex items-center gap-1 text-[11px] pl-2 pr-1 py-0.5 rounded-full bg-[#1D9E75]/15 border border-[#1D9E75]/40 text-[#1D9E75]">
                    <button
                      onClick={() => setActionsFor(actionsFor === b.id ? null : b.id)}
                      className="hover:underline"
                      title={o ? `${meta?.label} · ${new Date(o.last_action_at).toLocaleDateString("fr-FR")} — cliquer pour journaliser` : "Cliquer pour journaliser une action"}
                      data-testid={`court-buyer-chip-${b.id}`}
                    >
                      {b.name}
                    </button>
                    {meta && (
                      <span className="text-[9px] px-1 py-0 rounded-full font-medium" style={{ background: `${meta.color}22`, color: meta.color, border: `1px solid ${meta.color}55` }} data-testid={`court-buyer-status-${b.id}`}>
                        {meta.label}
                      </span>
                    )}
                    <button
                      onClick={() => setEmailBuyer(b)}
                      className="p-0.5 rounded-full hover:bg-[#1D9E75]/25 text-[#1D9E75]"
                      title={`Générer un email teaser pour ${b.name}`}
                      data-testid={`court-buyer-email-${b.id}`}
                    >
                      <Mail className="w-3 h-3" />
                    </button>
                    {actionsFor === b.id && (
                      <span className="absolute z-20 top-full left-0 mt-1 flex flex-col bg-[#0A0A0A] border border-white/15 rounded-md shadow-xl overflow-hidden" data-testid={`court-buyer-actions-${b.id}`}>
                        {Object.entries(OUTREACH_META).map(([k, v]) => (
                          <button key={k} onClick={() => logOutreach(b.id, k)} className="text-left text-[11px] px-3 py-1.5 hover:bg-white/5 whitespace-nowrap" style={{ color: v.color }} data-testid={`court-buyer-action-${b.id}-${k}`}>
                            {v.label}
                          </button>
                        ))}
                      </span>
                    )}
                  </span>
                );
              })}
            </div>
          )}
          {profiles.map((p, i) => (
            <div key={i} className="text-[11px] text-zinc-400"><span className="text-zinc-200 font-medium">{p.profil}</span> — {p.pourquoi}</div>
          ))}
        </div>
      )}

      {ng && (
        <div className="noam-card p-4 border border-[#378ADD]/20" data-testid="court-intel-nego">
          <div className="text-[10px] uppercase tracking-wider text-[#378ADD] mb-2 flex items-center gap-1.5"><Handshake className="w-3 h-3" /> Conditions de négociation suggérées</div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-400 mb-1.5">
            {ng.prix_cible_chf > 0 && <span>Prix cible : <b className="text-zinc-200">{fmtChf(ng.prix_cible_chf)}</b></span>}
            {ng.commission_suggeree_pct > 0 && <span>Commission : <b className="text-zinc-200">{ng.commission_suggeree_pct}%</b></span>}
            {ng.exclusivite && <span>Exclusivité : <b className="text-zinc-200">{ng.exclusivite}</b></span>}
          </div>
          {(ng.conditions || []).length > 0 && (
            <ul className="text-[11px] text-zinc-500 list-disc list-inside mb-1">
              {ng.conditions.map((c, i) => <li key={i}>{c}</li>)}
            </ul>
          )}
          {ng.strategie && <p className="text-[11px] text-zinc-400">{ng.strategie}</p>}
        </div>
      )}

      {metacog.length > 0 && (
        <div className="noam-card p-4 border border-[#7F77DD]/25" data-testid="court-intel-metacog">
          <div className="text-[10px] uppercase tracking-wider text-[#B5AEF7] mb-2 flex items-center gap-1.5"><Lightbulb className="w-3 h-3" /> Métacognition — et maintenant ?</div>
          <div className="space-y-1.5">
            {metacog.map((s, i) => (
              <div key={i} className="flex items-start gap-2 text-[11px]">
                <span className="shrink-0 mt-0.5 px-1.5 py-0 rounded bg-white/5 border border-white/10 text-zinc-500 text-[9px] uppercase">{s.module}</span>
                <div><span className="text-zinc-200 font-medium">{s.title}</span> <span className="text-zinc-500">— {s.suggestion}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {emailBuyer && (
        <BuyerEmailModal
          mandate={mandate}
          buyer={emailBuyer}
          onClose={() => setEmailBuyer(null)}
          onSent={() => logOutreach(emailBuyer.id, "teaser_envoye")}
        />
      )}
    </div>
  );
}

/* V23.16 — Email teaser IA pour un acheteur pressenti. */
function BuyerEmailModal({ mandate, buyer, onClose, onSent }) {
  const [email, setEmail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logged, setLogged] = useState(false);

  const markSent = () => {
    if (!logged && onSent) { setLogged(true); onSent(); }
  };

  useEffect(() => {
    api.post(`/workspace/courtage/mandates/${mandate.id}/buyer-email`, { buyer_name: buyer.name })
      .then((r) => setEmail(r.data))
      .catch((e) => { notify.error(fmtError(e)); onClose(); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(`Objet : ${email.subject}\n\n${email.body}`);
      notify.success("Email copié dans le presse-papier.");
      markSent();
    } catch {
      notify.error("Copie impossible — sélectionnez le texte manuellement.");
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur flex items-center justify-center p-4" onClick={onClose}>
      <div className="noam-card w-full max-w-lg max-h-[90vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="court-buyer-email-modal">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-[#1D9E75]" /> Email teaser — {buyer.name}</div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="court-buyer-email-close"><X className="w-4 h-4" /></button>
        </div>
        {loading ? (
          <div className="py-10 text-center text-zinc-500 text-sm flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Rédaction personnalisée en cours…
          </div>
        ) : email && (
          <>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Objet</div>
            <div className="text-sm text-zinc-100 bg-black/30 rounded px-3 py-2 mb-3" data-testid="court-buyer-email-subject">{email.subject}</div>
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Message (anonymisé)</div>
            <div className="text-xs text-zinc-300 bg-black/30 rounded px-3 py-2.5 whitespace-pre-wrap leading-relaxed mb-4" data-testid="court-buyer-email-body">{email.body}</div>
            <div className="flex gap-2">
              <button onClick={copyAll} className="btn-primary text-xs flex-1 justify-center" data-testid="court-buyer-email-copy">
                <Copy className="w-3.5 h-3.5" /> Copier l'email
              </button>
              <a
                href={`mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`}
                onClick={markSent}
                className="btn-secondary text-xs flex-1 justify-center inline-flex items-center gap-1.5"
                data-testid="court-buyer-email-mailto"
              >
                <Mail className="w-3.5 h-3.5" /> Ouvrir dans ma messagerie
              </a>
            </div>
            <p className="text-[10px] text-zinc-600 mt-2">Pensez à joindre le Teaser PDF téléchargeable depuis la fiche du mandat.</p>
          </>
        )}
      </div>
    </div>
  );
}

function Kpi({ label, value }) {
  return (
    <div className="noam-card p-3">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-lg font-bold mt-1">{value}</div>
    </div>
  );
}

function Slider({ label, value, onChange, testid }) {
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-zinc-400 flex-1">{label}</span>
      <input type="range" min="1" max="10" value={value} onChange={(e) => onChange(parseInt(e.target.value))}
        className="w-40" data-testid={testid} />
      <span className="text-xs text-zinc-300 font-semibold w-10 text-right">{value}/10</span>
    </div>
  );
}

function NumField({ label, value, onChange, onBlur, testid }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <input type="number" step="any" value={value} onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
        onBlur={onBlur} className="noam-input w-full" data-testid={testid} />
    </label>
  );
}

function Line({ k, v }) {
  return (
    <div className="flex justify-between py-1">
      <span className="text-zinc-500">{k}</span>
      <span className="text-zinc-200">{v}</span>
    </div>
  );
}

function QualBadge({ q }) {
  const c = q.level === "high" ? "#1D9E75" : q.level === "medium" ? "#EF9F27" : "#E24B4A";
  return (
    <div className="noam-card p-4 min-w-[180px] text-center" data-testid="court-qual-badge">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">Qualification</div>
      <div className="text-3xl font-bold mt-1" style={{ color: c }}>{q.score}</div>
      <div className="text-[10px] text-zinc-500 mt-1">{q.level}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, title, desc, cta, onCta, testid }) {
  return (
    <div className="noam-card p-8 text-center" data-testid={testid}>
      <Icon className="w-8 h-8 text-[#378ADD] mx-auto opacity-60" />
      <div className="noam-display text-lg mt-3">{title}</div>
      <p className="text-zinc-400 text-sm mt-1 max-w-md mx-auto">{desc}</p>
      {cta && onCta && (
        <button onClick={onCta} className="btn-primary mt-4">
          <Plus className="w-3.5 h-3.5" /> {cta}
        </button>
      )}
    </div>
  );
}

function Loading() {
  const { t } = useTranslation();
  return (
    <div className="text-zinc-400 text-sm flex items-center gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> {t("common.loading", { defaultValue: "Chargement…" })}
    </div>
  );
}
